import React, { createContext, useContext, useMemo, useState } from 'react';

export type CaseNotesActivityTab = 'notes' | 'activity';

export interface CaseNotesActivityPanelContextValue {
  isOpen: boolean;
  activeTab: CaseNotesActivityTab;
  open: (tab: CaseNotesActivityTab) => void;
  select: (tab: CaseNotesActivityTab) => void;
  close: () => void;
}

// Non-throwing default (mirrors TabSelectionContext/useSlotRegistry) so a bar action rendered outside
// a provider -- an isolated story or unit test -- still renders; a click is just a no-op.
const DEFAULT_PANEL: CaseNotesActivityPanelContextValue = {
  isOpen: false,
  activeTab: 'notes',
  open: () => {},
  select: () => {},
  close: () => {}
};

const Ctx = createContext<CaseNotesActivityPanelContextValue>(DEFAULT_PANEL);

export const CaseNotesActivityPanelProvider: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<CaseNotesActivityTab>('notes');

  const value = useMemo<CaseNotesActivityPanelContextValue>(
    () => ({
      isOpen,
      activeTab,
      open: (tab) => {
        setActiveTab(tab);
        setIsOpen(true);
      },
      select: setActiveTab,
      close: () => setIsOpen(false)
    }),
    [isOpen, activeTab]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function useCaseNotesActivityPanel(): CaseNotesActivityPanelContextValue {
  return useContext(Ctx);
}
